import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import { Watch } from "@/decorators/watch";
import { emit } from "@/internals/events";
import { isEmptySlot } from "@/internals/slot";
import TriggerController, { type TriggeredElement } from "@/controllers/trigger-controller";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";
import styles from "./atlas-dialog.scss";
import "@/components/display/atlas-heading/atlas-heading";
import "@/components/display/atlas-icon-button/atlas-icon-button";

export type DialogProps = AtlasElementProps & {
    "open": boolean;
    "header": string;
    "hide-close-button": boolean;
    "size": "sm" | "md" | "lg";
    "vertical-actions": boolean;
    "block-actions": boolean;
};

/**
 * @dependency atlas-heading
 * @dependency atlas-icon-button
 *
 * @slot - Conteúdo do dialog
 * @slot actions - Botões de ação do dialog
 *
 * @event {CustomEvent} atlas-dialog-open - Evento emitido quando o dialog é aberto
 * @event {CustomEvent} atlas-dialog-close - Evento emitido quando o dialog é fechado
 *
 * @tag atlas-dialog
 */
@customElement("atlas-dialog")
export default class AtlasDialog extends AtlasElement implements TriggeredElement {
    static styles = styles;

    /** Indica se o dialog está aberto ou não */
    @property({ type: Boolean, reflect: true }) open: boolean;

    /** Título do dialog */
    @property({ type: String }) header: string = "";

    /** Indica se o botão de fechar deve ser ocultado */
    @property({ type: Boolean, attribute: "hide-close-button", reflect: true }) hideCloseButton: boolean = false;

    /** Tamanho do dialog */
    @property({ type: String }) size: "sm" | "md" | "lg" = "md";

    /** Indica se as ações devem ser dispostas verticalmente */
    @property({ type: Boolean, attribute: "vertical-actions", reflect: true }) verticalActions: boolean = false;

    /** Indica se as ações devem ocupar toda a largura do dialog */
    @property({ type: Boolean, attribute: "block-actions", reflect: true }) blockActions: boolean = false;

    @state() private _hasContent = false;

    @state() private _hasActions = false;

    private _triggerController: TriggerController;

    constructor() {
        super();

        this.openDialog = this.openDialog.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.onSlottedButtonClick = this.onSlottedButtonClick.bind(this);

        this._triggerController = new TriggerController(this, "click");
    }

    /**
     * @internal
     */
    public connectedCallback() {
        super.connectedCallback();

        this.classList.add("disabled-animation");
        this.addEventListener("atlas-button-click", this.onSlottedButtonClick);
    }

    /**
     * @internal
     */
    public disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener("atlas-button-click", this.onSlottedButtonClick);
    }

    /**
     * @internal
     */
    public firstUpdated() {
        requestAnimationFrame(() => {
            this.classList.remove("disabled-animation");
        });
    }

    /**
     * Abre o dialog
     */
    public openDialog() {
        this._triggerController.show();
    }

    /**
     * Fecha o dialog
     */
    public closeDialog() {
        this._triggerController.hide();
    }

    /**
     * @internal
     */
    public onTriggerActivate() {
        this.open = this._triggerController.open;
        this._triggerController.triggerElement?.blur();
    }

    /**
     * @internal
     */
    @Watch("open")
    public onChangeOpenState() {
        if (this.open) {
            this.onOpenDialog();
        } else {
            this.onCloseDialog();
        }
    }

    private onSlottedButtonClick(event: CustomEvent) {
        const button = event.target as HTMLElement;

        if (button.hasAttribute("data-close-dialog")) {
            this.closeDialog();
        }
    }

    private onOpenDialog() {
        emit(this, "atlas-dialog-open");
    }

    private onCloseDialog() {
        emit(this, "atlas-dialog-close");
    }

    private async handleSlotChange() {
        await this.updateComplete;
        this._hasContent = !isEmptySlot(this);
    }

    private async handleActionsSlotChange() {
        await this.updateComplete;
        this._hasActions = !isEmptySlot(this, "actions");

        if (!this._hasActions) return;

        const actionsSlot = this.shadowRoot.querySelector("slot[name='actions']") as HTMLSlotElement;
        const slottedElements = actionsSlot.assignedElements({ flatten: true });

        slottedElements.forEach((element) => {
            element.toggleAttribute("block", this.blockActions || this.verticalActions);
        });
    }

    private renderCloseButton() {
        return when(
            !this.hideCloseButton,
            () => html`<atlas-icon-button icon="x" size="2x" @click=${this.closeDialog}></atlas-icon-button>`
        );
    }

    private renderHeader() {
        return when(
            this.header || !this.hideCloseButton,
            () => html`
                <div class="header">
                    <atlas-heading size="h6">${this.header}</atlas-heading>
                    ${this.renderCloseButton()}
                </div>
            `
        );
    }

    /**
     * @internal
     */
    public render() {
        const dialogClass = {
            "atlas-dialog": true,
            "has-content": this._hasContent,
            "has-actions": this._hasActions,
            "vertical-actions": this.verticalActions,
            "block-actions": this.blockActions,
            [this.size]: !!this.size
        };

        return html`
            <div class="${classMap(dialogClass)}">
                ${this.renderHeader()}
                <div class="content">
                    <slot @slotchange=${this.handleSlotChange}></slot>
                </div>
                <div class="footer">
                    <slot name="actions" @slotchange=${this.handleActionsSlotChange}></slot>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-dialog": AtlasDialog;
    }
}
